import React from 'react';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const CriticalIssuesPanel = ({ issues, unacknowledgedCount, onAcknowledge, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-900',
          icon: 'text-red-600',
          badge: 'bg-red-600 text-white'
        };
      case 'high':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          text: 'text-orange-900',
          icon: 'text-orange-600',
          badge: 'bg-orange-600 text-white'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-900',
          icon: 'text-yellow-600',
          badge: 'bg-yellow-600 text-white'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-900',
          icon: 'text-gray-600',
          badge: 'bg-gray-600 text-white'
        };
    }
  };

  const getIssueIcon = (eventType) => {
    switch (eventType) {
      case 'PAYMENT_FAILURE':
        return 'CreditCard';
      case 'USER_SIGNUP':
        return 'UserX';
      case 'SYSTEM_ANOMALY':
        return 'AlertTriangle';
      default:
        return 'AlertCircle';
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
          <Icon name="AlertOctagon" size={20} strokeWidth={2} />
          Critical Issues
        </h2>
        <div className="flex items-center gap-3">
          {unacknowledgedCount > 0 && (
            <div className="px-3 py-1 bg-red-600 text-white rounded-full text-sm font-semibold flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              {unacknowledgedCount} Unacknowledged
            </div>
          )}
        </div>
      </div>
      {issues?.length === 0 ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-4">
            <Icon name="CheckCircle" size={32} strokeWidth={2} className="text-green-600" />
          </div>
          <p className="text-lg font-semibold text-foreground mb-2">All Clear!</p>
          <p className="text-sm text-muted-foreground">
            No critical issues detected in the selected time range
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {issues?.map((issue) => {
            const colors = getSeverityColor(issue?.eventSeverity);
            const icon = getIssueIcon(issue?.eventType);

            return (
              <div
                key={issue?.id}
                className={`${colors?.bg} border ${colors?.border} rounded-lg p-4 ${
                  issue?.acknowledged ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <Icon 
                      name={icon} 
                      size={24} 
                      strokeWidth={2} 
                      className={`${colors?.icon} flex-shrink-0 mt-0.5`} 
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-semibold ${colors?.text}`}>
                          {issue?.eventType?.replace(/_/g, ' ')}
                        </h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colors?.badge}`}>
                          {issue?.eventSeverity?.toUpperCase()}
                        </span>
                      </div>
                      <p className={`text-sm ${colors?.text} mb-2`}>
                        {issue?.eventCategory || 'System Error'}
                      </p>
                      {issue?.eventData?.error && (
                        <p className="text-xs text-muted-foreground font-mono bg-white/50 p-2 rounded border border-gray-200 mb-2">
                          {issue?.eventData?.error}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(issue.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                      </p>
                    </div>
                  </div>
                  
                  {!issue?.acknowledged && (
                    <button
                      onClick={() => onAcknowledge(issue?.id)}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 flex-shrink-0"
                    >
                      <Icon name="Check" size={16} strokeWidth={2} />
                      Acknowledge
                    </button>
                  )}
                  
                  {issue?.acknowledged && (
                    <div className="flex items-center gap-2 text-sm text-green-600 flex-shrink-0">
                      <Icon name="CheckCircle" size={16} strokeWidth={2} />
                      Acknowledged
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CriticalIssuesPanel;